import React, { useEffect, useState, useRef } from "react";
import LayoutBuyerSeller from "../../components/layout/LayoutBuyerSeller";
import { FiArrowLeft, FiDownload } from "react-icons/fi";
import { useNavigate, useParams, Link } from "react-router-dom";
import apiService from "../../actions/apiService";
import Toast from "../../components/Toast";
import html2pdf from "html2pdf.js";

const BuyerInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const invoiceRef = useRef(null);

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError("");
      setReceipt(null);
      try {
        const result = await apiService.get(`/transactions/${id}/receipt`);
        if (!active) return;
        if (result.success && result.data?.receipt) {
          setReceipt(result.data.receipt);
        } else {
          setError(result.error || result.message || "Invoice not found");
        }
      } catch (err) {
        if (!active) return;
        // Handle different error types
        if (err.response?.status === 404) {
          setError("Invoice not found");
        } else if (err.response?.status === 403) {
          setError("Access denied");
        } else if (err.response?.status === 400) {
          setError(
            err.response?.data?.message ||
              err.message ||
              "Invoice not available"
          );
        } else {
          setError(err.message || "Failed to load invoice");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [id]);

  const handleDownload = () => {
    if (!invoiceRef.current) return;

    const element = invoiceRef.current;
    const referenceCode =
      receipt?.transaction?.reference_code ||
      receipt?.transaction?.id?.substring(0, 8) ||
      "invoice";
    const filename = `Invoice_${referenceCode}.pdf`;

    const opt = {
      margin: [10, 10, 10, 10],
      filename: filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    html2pdf().set(opt).from(element).save();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    if (amount == null) return "—";
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    return `€${num.toFixed(2)}`;
  };

  if (loading) {
    return (
      <LayoutBuyerSeller>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <div className="text-lg text-gray-600">Loading invoice...</div>
          </div>
        </div>
      </LayoutBuyerSeller>
    );
  }

  if (error || !receipt) {
    return (
      <LayoutBuyerSeller>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="text-red-600 text-lg mb-4">
              {error || "Invoice not found"}
            </div>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </LayoutBuyerSeller>
    );
  }

  const { transaction, item, buyer, seller, bank_account_detail } = receipt;

  return (
    <LayoutBuyerSeller>
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header with back button and actions */}
          <div className="mb-6 flex items-center justify-between">
            <div className="text-[14px] flex items-center justify-start text-[#2A8BF7] font-[600]">
              <Link
                className="flex items-center justify-center gap-1"
                to="/buyer/listings"
              >
                <FiArrowLeft className="w-3.5 h-3.5" /> Back to listings
              </Link>
            </div>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <FiDownload className="text-lg" />
              <span>Download</span>
            </button>
          </div>

          {/* Invoice Card */}
          <div
            ref={invoiceRef}
            className="bg-white rounded-lg shadow-lg p-8 print:shadow-none"
          >
            {/* Invoice Header */}
            <div className="border-b border-gray-200 pb-6 mb-6">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Invoice
                  </h1>
                  <p className="text-gray-600">
                    Transaction #
                    {transaction.reference_code ||
                      transaction.id.substring(0, 8)}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">Date</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatDate(transaction.completed_at)}
                  </div>
                </div>
              </div>
            </div>

            {/* Buyer and Seller Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                  From (Seller)
                </h3>
                <div className="text-gray-900">
                  <div className="font-semibold text-lg mb-1">
                    {seller.firstName} {seller.lastName}
                  </div>
                  {seller.business_information?.company_name && (
                    <div className="text-sm text-gray-600 mb-1">
                      {seller.business_information.company_name}
                    </div>
                  )}
                  <div className="text-sm text-gray-600">{seller.email}</div>
                  {seller.phone && (
                    <div className="text-sm text-gray-600">{seller.phone}</div>
                  )}
                  {seller.business_information?.vat_number && (
                    <div className="text-sm text-gray-600 mt-1">
                      VAT: {seller.business_information.vat_number}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                  To (Buyer)
                </h3>
                <div className="text-gray-900">
                  <div className="font-semibold text-lg mb-1">
                    {buyer.firstName} {buyer.lastName}
                  </div>
                  {buyer.business_information?.company_name && (
                    <div className="text-sm text-gray-600 mb-1">
                      {buyer.business_information.company_name}
                    </div>
                  )}
                  <div className="text-sm text-gray-600">{buyer.email}</div>
                  {buyer.phone && (
                    <div className="text-sm text-gray-600">{buyer.phone}</div>
                  )}
                  {buyer.business_information?.vat_number && (
                    <div className="text-sm text-gray-600 mt-1">
                      VAT: {buyer.business_information.vat_number}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Item Details */}
            <div className="border-t border-gray-200 pt-6 mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">
                Item Details
              </h3>
              <div className="flex gap-4">
                {item.images && item.images.length > 0 && (
                  <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={item.images[0].file_url || item.images[0].file_path}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h4>
                  {item.reference_code && (
                    <div className="text-sm text-gray-600 mb-1">
                      Reference: {item.reference_code}
                    </div>
                  )}
                  {(item.plate_number || item.category) && (
                    <div className="text-sm text-gray-600">
                      {item.plate_number && `Plate: ${item.plate_number}`}
                      {item.plate_number && item.category && " • "}
                      {item.category && (
                        <span className="capitalize">
                          {item.custom_category || item.category}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Details */}
            <div className="border-t border-gray-200 pt-6 mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">
                Payment Details
              </h3>
              <div className="space-y-3">
                {transaction.payment_reference && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Reference:</span>
                    <span className="font-medium text-gray-900">
                      {transaction.payment_reference}
                    </span>
                  </div>
                )}
                {transaction.bank_transfer_reference && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Bank Transfer Reference:
                    </span>
                    <span className="font-medium text-gray-900">
                      {transaction.bank_transfer_reference}
                    </span>
                  </div>
                )}
                {transaction.allpay_payment_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment ID:</span>
                    <span className="font-medium text-gray-900">
                      {transaction.allpay_payment_id}
                    </span>
                  </div>
                )}
                {bank_account_detail && (
                  <div className="space-y-2 mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payout Method:</span>
                      <span className="font-medium text-gray-900">
                        {bank_account_detail.preferred_payout_method}
                      </span>
                    </div>
                    {bank_account_detail.bank_name && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bank:</span>
                        <span className="font-medium text-gray-900">
                          {bank_account_detail.bank_name}
                        </span>
                      </div>
                    )}
                    {bank_account_detail.account_holder_name && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Account Holder:</span>
                        <span className="font-medium text-gray-900">
                          {bank_account_detail.account_holder_name}
                        </span>
                      </div>
                    )}
                    {bank_account_detail.iban && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">IBAN:</span>
                        <span className="font-medium text-gray-900">
                          {bank_account_detail.iban}
                        </span>
                      </div>
                    )}
                    {bank_account_detail.swift_bic && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">SWIFT/BIC:</span>
                        <span className="font-medium text-gray-900">
                          {bank_account_detail.swift_bic}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Invoice Summary */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">
                Summary
              </h3>
              <div className="space-y-3 mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Item Price:</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(transaction.sale_price || item?.price || 0)}
                  </span>
                </div>
                {transaction.is_delivered_door &&
                  transaction.delivery_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Delivery Fee:</span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(transaction.delivery_fee || 0)}
                      </span>
                    </div>
                  )}
                <div className="flex justify-between text-gray-600">
                  <span>Platform Fee:</span>
                  <span>{formatCurrency(transaction.platform_fee || 0)}</span>
                </div>
              </div>
              <div className="border-t-2 border-gray-300 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">
                    Total Paid:
                  </span>
                  <span className="text-2xl font-bold text-gray-900">
                    {formatCurrency(transaction.buyer_paid_amount || 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {transaction.notes && (
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
                  Notes
                </h3>
                <p className="text-gray-600 whitespace-pre-wrap">
                  {transaction.notes}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-500 print:hidden">
            <p>This is an official invoice for your transaction.</p>
            <p className="mt-1">
              Transaction completed on {formatDate(transaction.completed_at)}
            </p>
          </div>
        </div>
      </div>

      {toastMessage && (
        <Toast
          message={toastMessage}
          onClose={() => setToastMessage("")}
          type="success"
        />
      )}
    </LayoutBuyerSeller>
  );
};

export default BuyerInvoice;
